// Admin login: two-step. Screen 1 = email + password -> POST /admin/login, which
// returns either { stage:"totp_required", challenge } or { stage:"enroll_required",
// challenge, otpauth_url, totp_secret } (first login). Screen 2 = enter the 6-digit
// code (and, on first login, scan the QR first) -> POST /admin/login/verify ->
// { access_token, role, expires_at } + HttpOnly refresh cookie. The access token is
// stashed in memory only; the challenge + secret live in component state and are
// never persisted.

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

import { api } from "@/api/client";
import { setAccessToken } from "@/lib/auth";
import { errMessage } from "@/lib/errMessage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const credentialsSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});
type CredentialsValues = z.infer<typeof credentialsSchema>;

const codeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code"),
});
type CodeValues = z.infer<typeof codeSchema>;

interface BeginResponse {
  stage: "totp_required" | "enroll_required";
  challenge: string;
  otpauth_url?: string;
  totp_secret?: string;
}
interface VerifyResponse {
  access_token: string;
  role: string;
  expires_at: number;
}

export function LoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<"credentials" | "code">("credentials");
  const [challenge, setChallenge] = useState("");
  const [enroll, setEnroll] = useState<{ otpauth: string; secret: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const credForm = useForm<CredentialsValues>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: "", password: "" },
  });
  const codeForm = useForm<CodeValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: "" },
  });

  async function onCredentials(values: CredentialsValues) {
    setFormError(null);
    try {
      const res = await api.post<BeginResponse>("/admin/login", values, { anonymous: true });
      setChallenge(res.challenge);
      setEnroll(
        res.stage === "enroll_required" && res.otpauth_url && res.totp_secret
          ? { otpauth: res.otpauth_url, secret: res.totp_secret }
          : null,
      );
      setStep("code");
    } catch (err) {
      setFormError(errMessage(err));
    }
  }

  async function onCode(values: CodeValues) {
    setFormError(null);
    try {
      const res = await api.post<VerifyResponse>(
        "/admin/login/verify",
        { challenge, code: values.code },
        { anonymous: true },
      );
      setAccessToken(res.access_token);
      navigate("/ledger");
    } catch (err) {
      setFormError(errMessage(err));
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Admin sign in</CardTitle>
          <CardDescription>
            {step === "credentials"
              ? "Enter your email and password."
              : enroll
                ? "Scan the QR with your authenticator app, then enter the code."
                : "Enter the code from your authenticator app."}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {formError && (
            <Alert variant="destructive">
              <AlertTitle>Sign in failed</AlertTitle>
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          {step === "credentials" && (
            <Form {...credForm}>
              <form onSubmit={credForm.handleSubmit(onCredentials)} className="grid gap-4" noValidate>
                <FormField
                  control={credForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" autoComplete="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={credForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="current-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={credForm.formState.isSubmitting}>
                  Continue
                </Button>
              </form>
            </Form>
          )}

          {step === "code" && (
            <>
              {enroll && (
                <>
                  <div className="flex justify-center rounded-lg bg-white p-4">
                    <QRCodeSVG value={enroll.otpauth} size={176} />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Can&apos;t scan? Enter this secret manually:
                  </p>
                  <code className="block rounded bg-muted px-2 py-1 text-center text-sm break-all">
                    {enroll.secret}
                  </code>
                </>
              )}
              <Form {...codeForm}>
                <form onSubmit={codeForm.handleSubmit(onCode)} className="grid gap-4" noValidate>
                  <FormField
                    control={codeForm.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Code</FormLabel>
                        <FormControl>
                          <Input
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={6}
                            placeholder="123456"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full" disabled={codeForm.formState.isSubmitting}>
                    Sign in
                  </Button>
                </form>
              </Form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
